import React, { useState } from "react";
import { Box, Chip, IconButton, TextField, Typography } from "@mui/material";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import EventBusyOutlined from "@mui/icons-material/EventBusyOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import EditCalendarOutlined from "@mui/icons-material/EditCalendarOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { notifyError, notifySuccess } from "../../../components/CustomToast";
import { updateCongeConsomme } from "../../../services/CongeService";
import { AppButton } from "../../../components/UI";
import ConfirmDialog from "./ConfirmDialog";

const card = { borderRadius: "14px", background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,0.05)" };
const fmt = (n) => (Number.isInteger(n) ? n : Number(n || 0).toFixed(1).replace(".", ","));
const QUICK = [0.5, 1, 2, 5];

// "Poser" = consume days (delta > 0), "Restituer" = give days back (delta < 0): the API takes a signed delta.
const MODES = {
    poser: { label: "Poser des jours", Icon: EventBusyOutlined, color: "#b91c1c", bg: "#fef2f2", border: "#fca5a5", sign: 1 },
    restituer: { label: "Restituer des jours", Icon: EventAvailableOutlined, color: "#166534", bg: "#f0fdf4", border: "#86efac", sign: -1 },
};

export default function ConsoEditor({ collaborateurId, year, solde, onOptimisticUpdate, onSaved }) {
    const [mode, setMode] = useState("poser");
    const [days, setDays] = useState(1);
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState(false);

    const m = MODES[mode];
    const delta = m.sign * days;
    const droits = (solde?.ouverture ?? 0) + (solde?.acquis ?? 0) + (solde?.reporte ?? 0) + (solde?.ajustement ?? 0);
    const consomme = solde?.consomme ?? 0;
    const disponible = solde?.disponible ?? Math.max(0, droits - consomme);
    const nextConsomme = Math.max(0, consomme + delta);
    const nextDisponible = Math.max(0, droits - nextConsomme);

    const tooMany = mode === "poser" && days > disponible;
    const tooFewToReturn = mode === "restituer" && days > consomme;
    const invalid = !days || days <= 0 || tooMany || tooFewToReturn;

    const setDaysSafe = (v) => setDays(Math.max(0, Math.round(Number(v || 0) * 2) / 2));

    const doSave = async () => {
        try {
            setLoading(true);
            await updateCongeConsomme(collaborateurId, { annee: year, delta });
            onOptimisticUpdate?.((prev) => {
                const total = (prev?.ouverture ?? 0) + (prev?.acquis ?? 0) + (prev?.reporte ?? 0) + (prev?.ajustement ?? 0);
                const cons = Math.max(0, (prev?.consomme ?? 0) + delta);
                return { ...prev, consomme: cons, disponible: Math.max(0, total - cons) };
            });
            onSaved?.();
            notifySuccess(mode === "poser" ? `${fmt(days)} j posé${days > 1 ? "s" : ""}.` : `${fmt(days)} j restitué${days > 1 ? "s" : ""}.`);
            setDays(1);
        } catch {
            notifyError("Échec de la mise à jour.");
        } finally {
            setLoading(false);
            setConfirm(false);
        }
    };

    const onSave = () => (days >= 3 ? setConfirm(true) : doSave());

    return (
        <Box sx={{ ...card, p: 2, display: "flex", flexDirection: "column", gap: 1.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", background: "#ebf4fb", color: "#03407b" }}>
                    <EditCalendarOutlined sx={{ fontSize: 19 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a" }}>Enregistrer un mouvement</Typography>
                    <Typography sx={{ fontSize: "0.76rem", color: "#64748b" }}>Jours de congé posés ou rendus au collaborateur</Typography>
                </Box>
            </Box>

            <Box role="radiogroup" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                {Object.entries(MODES).map(([key, s]) => {
                    const active = mode === key;
                    return (
                        <Box key={key} role="radio" aria-checked={active} tabIndex={0}
                            onClick={() => setMode(key)}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setMode(key)}
                            sx={{
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1.1, borderRadius: "10px", cursor: "pointer",
                                fontWeight: 700, fontSize: "0.88rem", userSelect: "none", transition: "all 0.15s ease",
                                color: active ? s.color : "#64748b", background: active ? s.bg : "#fff",
                                border: `1.5px solid ${active ? s.border : "#e2e8f0"}`,
                                "&:hover": { borderColor: s.border }, "&:focus-visible": { outline: "2px solid #aed6f1" },
                            }}>
                            <s.Icon sx={{ fontSize: 19 }} /> {s.label}
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #e2e8f0", borderRadius: "10px", background: "#fff" }}>
                    <IconButton size="small" onClick={() => setDaysSafe(days - 0.5)} disabled={days <= 0.5} aria-label="Moins une demi-journée">
                        <RemoveRounded fontSize="small" />
                    </IconButton>
                    <TextField
                        variant="standard"
                        type="number"
                        value={days}
                        onChange={(e) => setDaysSafe(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !invalid && onSave()}
                        sx={{ width: 64, "& input": { textAlign: "center", fontSize: "1.1rem", fontWeight: 800, color: m.color } }}
                        slotProps={{ input: { disableUnderline: true }, htmlInput: { step: 0.5, min: 0.5 } }}
                    />
                    <IconButton size="small" onClick={() => setDaysSafe(days + 0.5)} aria-label="Plus une demi-journée">
                        <AddRounded fontSize="small" />
                    </IconButton>
                </Box>
                <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>jour{days > 1 ? "s" : ""}</Typography>
                <Box sx={{ display: "flex", gap: 0.5, ml: { sm: "auto" } }}>
                    {QUICK.map((q) => (
                        <Chip key={q} size="small" label={`${fmt(q)} j`} onClick={() => setDays(q)}
                            sx={{ height: 24, fontWeight: 600, color: days === q ? "#fff" : "#475569", background: days === q ? "#03407b" : "#f1f5f9", "&:hover": { background: days === q ? "#02357a" : "#e2e8f0" } }} />
                    ))}
                </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                {[
                    { label: "Consommés", from: consomme, to: nextConsomme },
                    { label: "Disponibles", from: disponible, to: nextDisponible },
                ].map((r) => (
                    <Box key={r.label} sx={{ px: 1.5, py: 1, borderRadius: "10px", background: "#f8fafc", border: "1px solid #f1f5f9" }}>
                        <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>{r.label}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#94a3b8" }}>{fmt(r.from)}</Typography>
                            <ArrowForward sx={{ fontSize: 15, color: "#cbd5e1" }} />
                            <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: r.to !== r.from ? m.color : "#0f172a" }}>{fmt(r.to)} j</Typography>
                        </Box>
                    </Box>
                ))}
            </Box>

            {(tooMany || tooFewToReturn) && (
                <Typography sx={{ fontSize: "0.8rem", color: "#b91c1c", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "8px", px: 1.25, py: 0.75 }}>
                    {tooMany
                        ? `Seulement ${fmt(disponible)} j disponible${disponible > 1 ? "s" : ""} : réduisez le nombre de jours.`
                        : `Seulement ${fmt(consomme)} j consommé${consomme > 1 ? "s" : ""} : impossible d'en restituer plus.`}
                </Typography>
            )}

            <AppButton
                variant={mode === "poser" ? "danger" : "primary"}
                onClick={onSave}
                disabled={invalid}
                loading={loading}
                startIcon={<m.Icon fontSize="small" />}
                sx={mode === "restituer" ? { background: "#166534 !important", "&:hover": { background: "#14532d !important" } } : undefined}
            >
                {mode === "poser" ? `Poser ${fmt(days)} jour${days > 1 ? "s" : ""}` : `Restituer ${fmt(days)} jour${days > 1 ? "s" : ""}`}
            </AppButton>

            <ConfirmDialog
                open={confirm}
                onClose={() => setConfirm(false)}
                onConfirm={doSave}
                loading={loading}
                text={mode === "poser"
                    ? `Poser ${fmt(days)} jours de congé pour ce collaborateur en ${year} ?`
                    : `Restituer ${fmt(days)} jours de congé à ce collaborateur en ${year} ?`}
            />
        </Box>
    );
}
